import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CupomService, ListarCuponsFiltro } from '../../../core/cupom/cupom.service';
import { CupomListado } from '../../../core/cupom/cupom.model';

const TIPO_LABELS: Record<string, string> = {
  P: 'Todos',
  C: 'B2C',
  E: 'Empresa',
  A: 'Assinantes',
  G: 'Geral',
};

const TIPO_DESC_LABELS: Record<string, string> = {
  P: '%',
  F: 'Preço fixo',
  R: 'R$ fixo',
  V: 'Voucher',
};

const PAGE_SIZE = 20;

@Component({
  selector: 'app-cupom-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="page">
      <header class="page__top">
        <div class="page__heading">
          <h1 class="page__title">Cupons</h1>
          <p class="page__sub">Templates de desconto configurados no sistema</p>
        </div>
        <div class="page__actions">
          <button class="btn" (click)="carregar()" [disabled]="loading()">
            {{ loading() ? 'Carregando…' : 'Recarregar' }}
          </button>
          <a class="btn btn--primary" routerLink="/cupons/novo">+ Novo cupom</a>
        </div>
      </header>

      <div class="filtros">
        <input class="search" type="text" placeholder="Buscar por codigo ou descricao..."
               [ngModel]="filtroBusca()" (ngModelChange)="onBuscaChange($event)" />
        <button class="chip" [class.chip--on]="filtroSit() === ''"  (click)="setSituacao('')">Todos</button>
        <button class="chip" [class.chip--on]="filtroSit() === 'A'" (click)="setSituacao('A')">Ativos</button>
        <button class="chip" [class.chip--on]="filtroSit() === 'I'" (click)="setSituacao('I')">Inativos</button>
        <button class="chip" [class.chip--on]="filtroSit() === 'S'" (click)="setSituacao('S')">Sazonais</button>
        <select class="select-tipo" [ngModel]="filtroTipo()" (ngModelChange)="setTipo($event)" aria-label="Filtrar por tipo">
          <option value="">Todos os tipos</option>
          <option value="P">Todos</option>
          <option value="C">B2C</option>
          <option value="E">Empresa</option>
          <option value="A">Assinantes</option>
          <option value="G">Geral</option>
        </select>
      </div>

      @if (loading()) {
        <div class="state">Carregando…</div>
      } @else if (erro()) {
        <div class="alert alert--erro">{{ erro() }}</div>
      } @else if (cupons().length === 0) {
        <div class="state">Nenhum cupom encontrado.</div>
      } @else {
        <table class="tbl">
          <thead>
            <tr>
              <th>#</th>
              <th>Código</th>
              <th>Descrição</th>
              <th>Tipo</th>
              <th>Desconto</th>
              <th>Vigência</th>
              <th>Situação</th>
              <th style="width: 80px"></th>
            </tr>
          </thead>
          <tbody>
            @for (c of cupons(); track c.nrSequencia) {
              <tr [class.row--inativo]="c.ieSituacao === 'I'">
                <td class="num">{{ c.nrSequencia }}</td>
                <td><a class="cod" [routerLink]="['/cupons', c.nrSequencia, 'editar']">{{ c.codCupom }}</a></td>
                <td>{{ c.dsCupom }}</td>
                <td>{{ tipoLabel(c.ieTipo) }}</td>
                <td>{{ descontoLabel(c) }}</td>
                <td class="muted">{{ vigencia(c) }}</td>
                <td>
                  <span class="status"
                        [class.status--inativo]="c.ieSituacao === 'I'"
                        [class.status--sazonal]="c.ieSituacao === 'S'">
                    {{ situacaoLabel(c.ieSituacao) }}
                  </span>
                </td>
                <td>
                  @if (c.ieSituacao === 'A') {
                    <button class="btn btn--sm btn--ghost-danger" (click)="inativar(c)" [disabled]="inativando()">Inativar</button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>

        <div class="pager">
          <span class="pager__info">
            {{ rangeInicio() }}–{{ rangeFim() }} de {{ total() }} cupons
          </span>
          <div class="pager__nav">
            <button class="btn btn--sm" (click)="irPara(1)" [disabled]="pagina() === 1">«</button>
            <button class="btn btn--sm" (click)="irPara(pagina() - 1)" [disabled]="pagina() === 1">‹ Anterior</button>
            <span class="pager__page">Página {{ pagina() }} de {{ totalPaginas() }}</span>
            <button class="btn btn--sm" (click)="irPara(pagina() + 1)" [disabled]="pagina() >= totalPaginas()">Próxima ›</button>
            <button class="btn btn--sm" (click)="irPara(totalPaginas())" [disabled]="pagina() >= totalPaginas()">»</button>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .page { padding: 32px 40px; max-width: 1200px; margin: 0 auto; font-family: var(--font-sans); color: rgb(var(--color-ink)); }
    .page__top { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 1px solid rgb(var(--color-line)); margin-bottom: 20px; gap: 16px; }
    .page__heading { display: flex; flex-direction: column; gap: 2px; }
    .page__title { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.02em; }
    .page__sub { margin: 0; color: rgb(var(--color-ink-3)); font-size: 13px; font-weight: 400; }
    .page__actions { display: flex; gap: 8px; flex-shrink: 0; padding-top: 4px; }

    .btn { padding: 9px 16px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); background: rgb(var(--color-paper)); color: rgb(var(--color-ink)); cursor: pointer; font-size: 13px; font-weight: 500; text-decoration: none; font-family: inherit; }
    .btn:hover:not(:disabled) { background: rgb(var(--color-hover)); }
    .btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .btn--primary { background: rgb(var(--color-accent)); color: #fff; border-color: rgb(var(--color-accent)); }
    .btn--primary:hover:not(:disabled) { background: rgb(var(--color-accent-2)); }

    .filtros { display: flex; gap: 8px; margin-bottom: 16px; align-items: center; flex-wrap: wrap; }
    .search { padding: 7px 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); font-size: 13px; min-width: 260px; outline: none; font-family: inherit; color: rgb(var(--color-ink)); background: rgb(var(--color-paper)); }
    .search:focus { border-color: rgb(var(--color-accent)); }
    .chip { padding: 6px 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: 999px; background: rgb(var(--color-paper)); font-size: 12px; cursor: pointer; color: rgb(var(--color-ink-2)); font-family: inherit; }
    .chip--on { background: rgb(var(--color-accent-bg)); color: rgb(var(--color-accent-2)); border-color: rgb(var(--color-accent)); font-weight: 600; }
    .select-tipo { padding: 6px 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); background: rgb(var(--color-paper)); font-size: 12px; cursor: pointer; color: rgb(var(--color-ink-2)); font-family: inherit; outline: none; }
    .select-tipo:focus { border-color: rgb(var(--color-accent)); }

    .state { padding: 28px; background: rgb(var(--color-paper)); border: 1px solid rgb(var(--color-line)); border-radius: var(--radius); color: rgb(var(--color-ink-3)); text-align: center; }
    .alert--erro { background: rgb(var(--color-danger-bg)); border: 1px solid rgb(var(--color-danger)); color: rgb(var(--color-danger)); padding: 12px 16px; border-radius: var(--radius); }

    .tbl { width: 100%; border-collapse: collapse; background: rgb(var(--color-paper)); border: 1px solid rgb(var(--color-line)); border-radius: var(--radius); overflow: hidden; font-size: 13px; }
    .tbl th { text-align: left; padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: rgb(var(--color-ink-3)); background: rgb(var(--color-bg-soft)); border-bottom: 1px solid rgb(var(--color-line)); }
    .tbl td { padding: 10px 14px; border-bottom: 1px solid rgb(var(--color-line)); vertical-align: middle; }
    .tbl tbody tr:last-child td { border-bottom: none; }
    .tbl .num { font-variant-numeric: tabular-nums; }
    .tbl .muted { color: rgb(var(--color-ink-3)); }
    .row--inativo td { color: rgb(var(--color-ink-3)); background: rgb(var(--color-bg-soft)); }

    .cod { font-family: var(--font-mono); font-weight: 600; color: rgb(var(--color-accent-2)); background: rgb(var(--color-accent-bg)); padding: 1px 6px; border-radius: 3px; font-size: 12px; text-decoration: none; cursor: pointer; }
    .cod:hover { text-decoration: underline; }

    .status { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: rgb(var(--color-accent-bg)); color: rgb(var(--color-accent-2)); text-transform: uppercase; letter-spacing: 0.04em; }
    .status--inativo { background: rgb(var(--color-hover)); color: rgb(var(--color-ink-3)); }
    .status--sazonal { background: rgb(var(--color-warn-bg)); color: rgb(var(--color-warn)); }

    .btn--sm { padding: 6px 12px; font-size: 12px; }
    .btn--ghost-danger { color: rgb(var(--color-danger)); border-color: transparent; background: transparent; font-family: inherit; }
    .btn--ghost-danger:hover:not(:disabled) { background: rgb(var(--color-danger-bg)); border-color: rgb(var(--color-danger)); }

    .pager { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; gap: 16px; flex-wrap: wrap; }
    .pager__info { color: rgb(var(--color-ink-3)); font-size: 13px; }
    .pager__nav { display: flex; gap: 6px; align-items: center; }
    .pager__page { font-size: 13px; color: rgb(var(--color-ink-2)); padding: 0 8px; font-variant-numeric: tabular-nums; }
  `],
})
export class CupomListComponent implements OnInit {
  private readonly cupomSvc = inject(CupomService);

  protected readonly cupons = signal<CupomListado[]>([]);
  protected readonly total = signal(0);
  protected readonly pagina = signal(1);
  protected readonly loading = signal(false);
  protected readonly erro = signal<string | null>(null);
  protected readonly filtroSit = signal<'' | 'A' | 'I' | 'S'>('A');
  protected readonly filtroTipo = signal<string>('');
  protected readonly filtroBusca = signal('');
  protected readonly inativando = signal(false);

  protected readonly totalPaginas = computed(() => Math.max(1, Math.ceil(this.total() / PAGE_SIZE)));
  protected readonly rangeInicio = computed(() => (this.total() === 0 ? 0 : (this.pagina() - 1) * PAGE_SIZE + 1));
  protected readonly rangeFim = computed(() => Math.min(this.pagina() * PAGE_SIZE, this.total()));

  private buscaTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void { this.carregar(); }

  carregar(): void {
    this.loading.set(true);
    this.erro.set(null);

    const filtro: ListarCuponsFiltro = {
      skip: (this.pagina() - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    };
    const termo = this.filtroBusca().trim();
    if (termo) filtro.search = termo;
    const sit = this.filtroSit();
    if (sit) filtro.ieSituacao = sit;
    const tipo = this.filtroTipo();
    if (tipo) filtro.ieTipo = tipo;

    this.cupomSvc.listar(filtro).subscribe({
      next: res => {
        this.cupons.set(res.data?.items ?? []);
        this.total.set(res.data?.total ?? 0);
        this.loading.set(false);
      },
      error: e => {
        this.erro.set(`Falha: ${e.status ?? ''} ${e.statusText ?? ''} — ${e.message}`);
        this.loading.set(false);
      },
    });
  }

  onBuscaChange(valor: string): void {
    this.filtroBusca.set(valor);
    if (this.buscaTimer) clearTimeout(this.buscaTimer);
    this.buscaTimer = setTimeout(() => {
      this.pagina.set(1);
      this.carregar();
    }, 350);
  }

  setSituacao(sit: '' | 'A' | 'I' | 'S'): void {
    if (this.filtroSit() === sit) return;
    this.filtroSit.set(sit);
    this.pagina.set(1);
    this.carregar();
  }

  setTipo(tipo: string): void {
    if (this.filtroTipo() === tipo) return;
    this.filtroTipo.set(tipo);
    this.pagina.set(1);
    this.carregar();
  }

  irPara(p: number): void {
    const destino = Math.min(Math.max(1, p), this.totalPaginas());
    if (destino === this.pagina()) return;
    this.pagina.set(destino);
    this.carregar();
  }

  inativar(c: CupomListado): void {
    if (!confirm(`Inativar o cupom ${c.codCupom}?`)) return;
    this.inativando.set(true);
    this.cupomSvc.inativar(c.nrSequencia).subscribe({
      next: () => {
        this.inativando.set(false);
        this.carregar();
      },
      error: () => {
        this.erro.set('Falha ao inativar cupom.');
        this.inativando.set(false);
      },
    });
  }

  tipoLabel(t: string): string { return TIPO_LABELS[t] ?? t; }

  descontoLabel(c: CupomListado): string {
    if (c.ieTipoDesconto === 'P' && c.prDesconto != null) return `${c.prDesconto}%`;
    if (c.ieTipoDesconto === 'V') return '100% (voucher)';
    if (c.vlDesconto != null) return `R$ ${c.vlDesconto}`;
    return TIPO_DESC_LABELS[c.ieTipoDesconto] ?? c.ieTipoDesconto;
  }

  vigencia(c: CupomListado): string {
    const ini = c.dtVigenciaIni ? this.fmt(c.dtVigenciaIni) : '—';
    const fim = c.dtVigenciaFim ? this.fmt(c.dtVigenciaFim) : 'sem prazo';
    return `${ini} → ${fim}`;
  }

  situacaoLabel(s: string): string {
    if (s === 'A') return 'Ativo';
    if (s === 'I') return 'Inativo';
    if (s === 'S') return 'Sazonal';
    return s;
  }

  private fmt(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('pt-BR');
  }
}
